/**
 * WidgetGallery — the Customize panel's catalogue.
 *
 * It is rendered through `GlobalSidePanel` (`type: 'dashboardWidgets'`) rather
 * than as a modal of its own, per the app's one-panel-system rule.
 *
 * FOUR THINGS IT LEARNED from being used.
 *
 *   • IT LOOKED EMPTY, AND IT WASN'T. The panel used to list only what was
 *     hidden — which, on a dashboard whose persona already places nine of the
 *     twelve widgets, is two rows. "Add a widget" offering "All Metrics" and
 *     "Notifications" reads as the whole of what a dashboard can be. It now
 *     lists the ENTIRE catalogue and marks what is already placed, so the panel
 *     shows the shape of the thing rather than its remainder.
 *   • IT STARTED IN THE MIDDLE. The "nothing left" branch centred itself
 *     vertically, and the list column was a stretched flex child, so on a tall
 *     panel the content floated. Everything is pinned to the top now.
 *   • IT IS GROUPED. Twelve rows in one undifferentiated column is a list you
 *     scan rather than read. The registry's `category` puts them under four
 *     headings, in the order the page itself is arranged.
 *   • THE PANEL STAYS OPEN. Adding a widget used to close it, so putting three
 *     cards back meant opening it three times. A row flips state in place, and
 *     — because placed widgets are listed too — it can be taken off again from
 *     the same row.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCheckLight,
  PiMagnifyingGlassLight,
  PiPlusLight,
  PiSquaresFourLight,
  PiXLight,
} from 'react-icons/pi';
import { Well } from '../shared/brand/Field';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import Input from '../shared/TailwindComponents/Input';
import Button from '../shared/TailwindComponents/Button';
import {
  DashboardWidgetId,
  WIDGETS,
  WIDGET_CATEGORIES,
  WIDGET_IDS,
} from './widgets/registry';

interface Props {
  /** Registered, hidden and licensed — in registration order. */
  available: DashboardWidgetId[];
  /** Registered, licensed and already on the dashboard. */
  placed?: DashboardWidgetId[];
  onAdd: (id: DashboardWidgetId) => void;
  /** Absent when the caller cannot remove — the row is then read-only. */
  onRemove?: (id: DashboardWidgetId) => void;
  onClose: () => void;
}

const WidgetGallery: React.FC<Props> = ({
  available,
  placed = [],
  onAdd,
  onRemove,
  onClose,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  /*
    What THIS visit changed. `available`/`placed` are recomputed by the grid
    behind the panel, so a row would otherwise simply swap sides the instant it
    was clicked — a jump is not an acknowledgement. These two win over the
    props for the ids they name.
  */
  const [added, setAdded] = useState<DashboardWidgetId[]>([]);
  const [removed, setRemoved] = useState<DashboardWidgetId[]>([]);

  const isOn = (id: DashboardWidgetId): boolean => {
    if (added.includes(id)) return true;
    if (removed.includes(id)) return false;
    return placed.includes(id);
  };

  /** Everything this licence can reach, placed or not. */
  const catalogue = useMemo(() => {
    const offerable = new Set<DashboardWidgetId>([...available, ...placed, ...added, ...removed]);
    return WIDGET_IDS.filter((id) => offerable.has(id) && !!WIDGETS[id]);
  }, [available, placed, added, removed]);

  const needle = query.trim().toLowerCase();
  const groups = useMemo(
    () =>
      WIDGET_CATEGORIES.map((category) => ({
        ...category,
        ids: catalogue.filter((id) => {
          const definition = WIDGETS[id];
          if (definition.category !== category.id) return false;
          if (!needle) return true;
          return `${t(definition.label)} ${t(definition.description)}`
            .toLowerCase()
            .includes(needle);
        }),
      })).filter((group) => group.ids.length > 0),
    [catalogue, needle, t]
  );

  const toAdd = catalogue.filter((id) => !isOn(id)).length;

  if (catalogue.length === 0) {
    return (
      // Pinned to the top like every other state of this panel — a lone empty
      // state floating in the vertical middle reads as a failed load.
      <div className="flex flex-1 flex-col justify-start p-4">
        <EmptyState
          icon={<PiSquaresFourLight />}
          headline={t('No widgets available')}
          subtext={t('Your plan does not cover any dashboard cards yet.')}
          button={{ label: t('Close'), onClick: onClose }}
        />
      </div>
    );
  }

  return (
    // `justify-start`, and a list column that is `flex-1` only so its OWN
    // scroller has a height — the rows themselves always start at the top.
    <div className="flex min-h-0 flex-1 flex-col justify-start gap-3 p-4">
      <div className="flex-shrink-0">
        <Input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('Search widgets')}
          icon={<PiMagnifyingGlassLight className="h-4 w-4" />}
        />
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {toAdd > 0
            ? t('{{count}} widgets are not on your dashboard yet.', { count: toAdd })
            : t('Every widget is on your dashboard. Switch one off to hide it.')}
        </p>
      </div>

      <div className="custom-scrollbar-main flex min-h-0 flex-1 flex-col items-stretch justify-start gap-4 overflow-y-auto">
        {groups.length === 0 ? (
          <EmptyState
            density="compact"
            icon={<PiMagnifyingGlassLight />}
            headline={t('No widgets match')}
            subtext={t('Try a different word, or clear the search.')}
            button={{ label: t('Clear'), onClick: () => setQuery('') }}
          />
        ) : (
          groups.map((group) => (
            <section key={group.id} data-testid={`widget-gallery-group-${group.id}`}>
              <p className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
                {t(group.label)}
              </p>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {t(group.description)}
              </p>

              <div className="mt-2 flex flex-col gap-2">
                {group.ids.map((id) => {
                  const definition = WIDGETS[id];
                  const Icon = definition.icon;
                  const on = isOn(id);
                  // Nothing to press when the row is on and the caller offers
                  // no way to take it off.
                  const inert = on && !onRemove;

                  const toggle = () => {
                    if (inert) return;
                    if (on) {
                      setRemoved((current) => [...current, id]);
                      setAdded((current) => current.filter((other) => other !== id));
                      onRemove?.(id);
                      return;
                    }
                    setAdded((current) => [...current, id]);
                    setRemoved((current) => current.filter((other) => other !== id));
                    onAdd(id);
                  };

                  return (
                    <Well
                      key={id}
                      data-testid={`widget-gallery-${id}`}
                      role="button"
                      aria-pressed={on}
                      tabIndex={inert ? -1 : 0}
                      aria-disabled={inert}
                      onClick={toggle}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggle();
                        }
                      }}
                      className={clsx(
                        'group flex items-start gap-3 text-left transition-colors duration-200 ease-slide focus:outline-none',
                        on
                          ? 'border-champagne/50'
                          : 'hover:border-champagne/60 focus:border-champagne/60',
                        inert ? 'cursor-default' : 'cursor-pointer'
                      )}
                    >
                      <span
                        className={clsx(
                          'mt-0.5 flex-shrink-0 transition-colors duration-200',
                          on ? 'text-champagne' : 'text-fg-muted'
                        )}
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                          {t(definition.label)}
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                          {on ? t('On your dashboard') : t(definition.description)}
                        </span>
                      </span>
                      {on ? (
                        <span
                          aria-hidden
                          className="mt-0.5 flex-shrink-0 text-champagne group-hover:text-danger-500"
                        >
                          <PiCheckLight className="h-4 w-4 group-hover:hidden" />
                          {onRemove && <PiXLight className="hidden h-4 w-4 group-hover:block" />}
                        </span>
                      ) : (
                        <PiPlusLight
                          aria-hidden
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 transition-colors duration-200 group-hover:text-champagne dark:text-gray-500"
                        />
                      )}
                    </Well>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>

      <div className="flex flex-shrink-0 justify-end border-t border-hairline-soft pt-3">
        <Button variant="primary" size="small" onClick={onClose}>
          {added.length > 0 || removed.length > 0 ? t('Done') : t('Close')}
        </Button>
      </div>
    </div>
  );
};

export default WidgetGallery;
